import {
  AggregatedStoppage,
  AggregatedStoppageCodeStatistics,
  IssueGroupingKey,
  StoppageCode,
  StoppageCodeStatistic
} from '@domain/stoppages'
import {DataContentWrapper} from '@hconnect/common/containers'
import {formatNumber} from '@hconnect/common/utils'
import {DateRange} from '@hconnect/uikit/src/common'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import {UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {sortBy, findIndex} from 'lodash'
import React, {Fragment, ReactNode, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useDepartmentNameMap, useTimeRange} from '../../hooks'
import {getBarTimeFormattedLabel, getTimeStep, isUnavailable} from '../../utils'
import {collectDepartmentUpmIdsFromStats} from '../../utils/stoppageFilter.helper'

type ColumnData = {
  key: string
  label: string | ReactNode
  width?: string
}

type Props = {
  stoppageConfig?: StoppageCode[]
  stoppageCodeStatsQuery: UseQueryResult<AggregatedStoppageCodeStatistics, AxiosError>
  sortedStoppages: AggregatedStoppage[]
  topIssuesGroupingKey: IssueGroupingKey
  timezone: string
}

type ColumnsConfig = {key: string; label: string}[]
const commonColumns: ColumnsConfig = [
  {key: 'stoppage-count-incident', label: 'stoppageCountIncident'},
  {key: 'stoppage-duration-incident', label: 'stoppageDurationIncident'},
  {key: 'stoppage-count-circumstance', label: 'stoppageCountCircumstance'},
  {key: 'stoppage-duration-circumstance', label: 'stoppageDurationCircumstance'}
]
const otherColumns = {
  stoppageCode: {key: 'code', label: 'stoppageCode'},
  stoppedMainEquipment: {key: 'stopped-main-equipment', label: 'stoppedMainEquipment'},
  mainEquipment: {key: 'main-equip', label: 'mainEquipment'},
  functionalLocation: {key: 'functional-location', label: 'functionalLocation'},
  totalStoppage: {key: 'total-stoppage', label: 'totalStoppage'},
  date: {key: 'date', label: 'date'}
}

const columns: Record<IssueGroupingKey, {key: string; label: string}[]> = {
  [IssueGroupingKey.STOPPAGE_CODE]: [
    otherColumns.stoppageCode,
    otherColumns.stoppedMainEquipment,
    otherColumns.mainEquipment,
    otherColumns.functionalLocation,
    otherColumns.totalStoppage,
    ...commonColumns
  ],
  [IssueGroupingKey.STOPPED_MAIN_EQUIPMENT]: [
    otherColumns.stoppedMainEquipment,
    otherColumns.mainEquipment,
    otherColumns.stoppageCode,
    otherColumns.functionalLocation,
    otherColumns.totalStoppage,
    ...commonColumns
  ],
  [IssueGroupingKey.MAIN_EQUIPMENT]: [
    otherColumns.mainEquipment,
    otherColumns.stoppedMainEquipment,
    otherColumns.stoppageCode,
    otherColumns.functionalLocation,
    otherColumns.totalStoppage,
    ...commonColumns
  ],
  [IssueGroupingKey.EQUIPMENT_NUMBER]: [
    otherColumns.functionalLocation,
    otherColumns.stoppageCode,
    otherColumns.stoppedMainEquipment,
    otherColumns.mainEquipment,
    otherColumns.totalStoppage,
    ...commonColumns
  ],
  [IssueGroupingKey.TIME_INTERVAL]: [
    otherColumns.date,
    otherColumns.stoppageCode,
    otherColumns.mainEquipment,
    otherColumns.functionalLocation,
    otherColumns.totalStoppage,
    ...commonColumns
  ]
}

export const StoppageStatsTable: React.FC<Props> = ({
  stoppageConfig = [],
  topIssuesGroupingKey,
  stoppageCodeStatsQuery,
  sortedStoppages,
  timezone
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const dateRange: DateRange<Date> = useTimeRange()
  const timeStep = getTimeStep(dateRange, timezone)
  const {
    data: stoppageCodeStats = {},
    isInitialLoading,
    isError,
    refetch,
    isRefetching
  } = stoppageCodeStatsQuery

  const showStoppedMainEquipment = topIssuesGroupingKey !== IssueGroupingKey.TIME_INTERVAL
  const departmentUpmIds = useMemo(
    () =>
      showStoppedMainEquipment
        ? collectDepartmentUpmIdsFromStats(sortedStoppages, stoppageCodeStats, topIssuesGroupingKey)
        : [],
    [showStoppedMainEquipment, sortedStoppages, stoppageCodeStats, topIssuesGroupingKey]
  )

  const departmentNameMap = useDepartmentNameMap({
    departmentUpmIds,
    isEnabled: showStoppedMainEquipment
  })

  const columnsData = useMemo(() => {
    const rows: ColumnData[][] = []
    sortedStoppages.forEach(({key}) => {
      const stoppageCodeStatistic: StoppageCodeStatistic | undefined = stoppageCodeStats[key]
      if (stoppageCodeStatistic) {
        const column = [
          ...(topIssuesGroupingKey === IssueGroupingKey.TIME_INTERVAL
            ? [
                {
                  key: 'date',
                  label: getBarTimeFormattedLabel({
                    dateUtc: key,
                    step: timeStep,
                    timezone,
                    language
                  })
                }
              ]
            : []),
          {
            key: 'code',
            label: (
              <>
                {stoppageCodeStatistic.stoppageCodes.map((stoppageCode) => {
                  const stoppageCodeInfo: StoppageCode | undefined = stoppageConfig.find(
                    ({code}) => code === stoppageCode
                  )
                  return (
                    <Fragment key={stoppageCode}>
                      <Typography variant="subtitle1">{stoppageCodeInfo?.text}</Typography>
                      <Typography variant="caption">{stoppageCode}</Typography>
                    </Fragment>
                  )
                })}
              </>
            )
          },
          ...(showStoppedMainEquipment
            ? [
                {
                  key: 'stopped-main-equipment',
                  label: (
                    <>
                      {stoppageCodeStatistic.stoppedDepartmentUpmIds?.length
                        ? stoppageCodeStatistic.stoppedDepartmentUpmIds.map((id) => (
                            <Typography key={id}>
                              {isUnavailable(id)
                                ? t('label.notAvailable')
                                : departmentNameMap.get(id) || id}
                            </Typography>
                          ))
                        : '-'}
                    </>
                  )
                }
              ]
            : []),
          {
            key: 'main-equip',
            label: (
              <>
                {stoppageCodeStatistic.mainEquipments.map((equipment) => (
                  <Typography key={equipment.id}>{equipment.text}</Typography>
                ))}
              </>
            )
          },

          {
            key: 'functional-location',
            label: (
              <>
                {stoppageCodeStatistic.equipments
                  ? stoppageCodeStatistic.equipments.map((equipment) => (
                      <Typography key={equipment.id}>{equipment.text}</Typography>
                    ))
                  : '-'}
              </>
            )
          },
          {
            key: 'total-stoppage',
            label: formatNumber(stoppageCodeStatistic.hoursStopped, language)
          },
          {
            key: 'stoppage-count-incident',
            label: stoppageCodeStatistic.incidentStoppagesCount,
            width: '50px'
          },
          {
            key: 'stoppage-duration-incident',
            label: formatNumber(stoppageCodeStatistic.incidentHoursStopped, language),
            width: '50px'
          },
          {
            key: 'stoppage-count-circumstance',
            label: stoppageCodeStatistic.circumstanceStoppagesCount,
            width: '50px'
          },
          {
            key: 'stoppage-duration-circumstance',
            label: formatNumber(stoppageCodeStatistic.circumstanceHoursStopped, language),
            width: '50px'
          }
        ]

        const sortedColumns = sortBy<ColumnData>(column, ({key}: ColumnData) => {
          const columnConfig: ColumnsConfig = columns[topIssuesGroupingKey]
          return findIndex(columnConfig, {key})
        })
        rows.push(sortedColumns)
      }
    })
    return rows
  }, [
    sortedStoppages,
    stoppageCodeStats,
    topIssuesGroupingKey,
    timeStep,
    language,
    timezone,
    stoppageConfig,
    showStoppedMainEquipment,
    departmentNameMap,
    t
  ])

  return (
    <DataContentWrapper<AggregatedStoppageCodeStatistics>
      showLoader={isInitialLoading}
      data={stoppageCodeStats}
      retryFunction={() => void refetch()}
      isError={isError}
      isRefetching={isRefetching}
    >
      <Paper>
        <TableContainer component="div" data-test-id="statistic-table">
          <Table>
            <TableHead>
              <TableRow>
                {columns[topIssuesGroupingKey].map((column) => (
                  <TableCell key={column.key} data-test-id={`stats-header-col-${column.key}`}>
                    {t(`label.${column.label}`)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {columnsData.length ? (
                columnsData.map((row, index) => {
                  return (
                    <TableRow key={index} data-test-id={`stat-row-${index}`}>
                      {row.map(({key, label, ...rest}) => {
                        return (
                          <TableCell {...rest} key={key} data-test-id={`stats-col-${index}-${key}`}>
                            {label}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns[topIssuesGroupingKey].length}
                    data-test-id="table-desc-empty"
                    sx={{
                      textAlign: 'center'
                    }}
                  >
                    {t('label.noData')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </DataContentWrapper>
  )
}
